import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  LinearProgress,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchPlayers,
  createPlayer,
  updatePlayer,
  removePlayer,
} from "@/store/playersSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import { PlayerDialog } from "@components/PlayerDialog";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { DataGridSurface } from "@components/DataGridSurface";
import { PAGE_CONTAINER_SX, dataGridFooterRadiusSx } from "@/constants/layout";
import type { Player } from "@/types";

export default function Players() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { items: players, loading } = useAppSelector((s) => s.players);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPlayer, setEditPlayer] = useState<Player | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPlayers());
  }, [dispatch]);

  const filtered = useMemo(
    () =>
      players.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [players, search],
  );

  const handleCreate = async (data: {
    name: string;
    defense: number;
    agility: number;
    technique: number;
  }) => {
    try {
      await dispatch(createPlayer(data)).unwrap();
      notify(t("players.created"), "success");
      setDialogOpen(false);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleUpdate = async (data: {
    name: string;
    defense: number;
    agility: number;
    technique: number;
  }) => {
    if (!editPlayer) return;
    try {
      await dispatch(updatePlayer({ id: editPlayer.id, data })).unwrap();
      notify(t("players.updated"), "success");
      setEditPlayer(null);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await dispatch(removePlayer(deleteId)).unwrap();
      notify(t("players.deleted"), "success");
      setDeleteId(null);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const openEdit = useCallback((player: Player) => {
    setEditPlayer(player);
  }, []);

  const openDelete = useCallback((playerId: string) => {
    setDeleteId(playerId);
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "name", headerName: t("players.name"), flex: 1, minWidth: 150 },
      {
        field: "defense",
        headerName: t("players.defense"),
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "agility",
        headerName: t("players.agility"),
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "technique",
        headerName: t("players.technique"),
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "overall",
        headerName: t("players.overall"),
        width: 100,
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) =>
          ((row.defense + row.agility + row.technique) / 3).toFixed(1),
      },
      {
        field: "bonusPoints",
        headerName: t("players.bonus"),
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box>
            <IconButton
              size="small"
              onClick={() => openEdit(params.row as Player)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openDelete(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [openDelete, openEdit, t],
  );

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("players.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("players.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t("players.addPlayer")}
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("players.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: "100%", sm: 300 } }}
        />
      </Box>
      <DataGridSurface>
        {loading && <LinearProgress />}
        <DataGrid
          rows={filtered}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={dataGridFooterRadiusSx}
        />
      </DataGridSurface>
      <PlayerDialog
        open={dialogOpen || !!editPlayer}
        player={editPlayer}
        onClose={() => {
          setDialogOpen(false);
          setEditPlayer(null);
        }}
        onSubmit={editPlayer ? handleUpdate : handleCreate}
      />
      <ConfirmDialog
        open={!!deleteId}
        title={t("players.deleteTitle")}
        message={t("players.deleteMessage")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
